import ResponsiveTable from '../ReusableComponents/ResponsiveTable';
import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { CircularProgress } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import { MdFileDownload, MdOutlineSmartToy, MdPlayArrow, MdRefresh } from "react-icons/md";
import { BsFillEyeFill } from "react-icons/bs";
import { Link } from "react-router-dom";
import { saveAs } from "file-saver";
import moment from "moment";
import Request from "../../Request";
import Search from "../ReusableComponents/Search";

const EVENT_TYPE_OPTIONS = [
    { value: "all", label: "Todos os tipos" },
    { value: "AI_ASSISTANT", label: "Assistente de IA" },
    { value: "BOOK", label: "Livro" },
    { value: "BROWSER_NEW_TAB", label: "Nova aba do navegador" },
    { value: "CALCULATOR", label: "Calculadora" },
    { value: "CELL_PHONE", label: "Celular" },
    { value: "EARBUDS", label: "Fones intra-auriculares" },
    { value: "EMAIL", label: "E-mail" },
    { value: "EXTERNAL_WEBSITE", label: "Site externo" },
    { value: "HEADPHONES", label: "Fones de ouvido" },
    { value: "LAPTOP", label: "Notebook" },
    { value: "LONG_HAIR", label: "Cabelo longo" },
    { value: "LOOKING_AWAY", label: "Olhar desviado" },
    { value: "MESSAGING", label: "Mensagens" },
    { value: "MULTIPLE_PEOPLE", label: "Múltiplas pessoas" },
    { value: "MULTIPLE_SPEAKERS", label: "Múltiplas vozes" },
    { value: "NO_PERSON", label: "Sem pessoas" },
    { value: "PAPER", label: "Papel" },
    { value: "PERSON", label: "Pessoa" },
    { value: "REFERENCE", label: "Referência" },
    { value: "SEARCH_ENGINE", label: "Buscador" },
    { value: "SECOND_SPEAKER", label: "Segunda voz" },
    { value: "SPEECH", label: "Fala detectada" },
    { value: "START_MENU", label: "Menu iniciar" },
    { value: "SMARTWATCH", label: "Relógio inteligente" },
    { value: "TV_MONITOR", label: "TV/Monitor" },
];

const SOURCE_OPTIONS = [
    { value: "all", label: "Todas as origens" },
    { value: "camera", label: "Câmera normal" },
    { value: "external_camera", label: "Câmera ambiente" },
    { value: "screen", label: "Tela" },
    { value: "image", label: "Imagem" },
];

const SORT_OPTIONS = [
    { value: "date_asc", label: "Data crescente" },
    { value: "date_desc", label: "Data decrescente" },
    { value: "events_desc", label: "Mais eventos" },
    { value: "events_asc", label: "Menos eventos" },
];

const EVENT_TYPE_LABELS = EVENT_TYPE_OPTIONS.reduce((labels, option) => {
    if (option.value) labels[option.value] = option.label;
    return labels;
}, {});
EVENT_TYPE_LABELS.NO_FACE = "Sem pessoas";

const SOURCE_LABELS = SOURCE_OPTIONS.reduce((labels, option) => {
    labels[option.value] = option.label;
    return labels;
}, {});

const SEVERITY_LABELS = {
    info: "Informativo",
    low: "Baixo",
    medium: "Médio",
    high: "Alto",
    critical: "Crítico",
};

const formatSeconds = (value) => {
    const seconds = Number(value || 0);
    if (!Number.isFinite(seconds)) return "-";
    const minutes = Math.floor(seconds / 60);
    const rest = Math.floor(seconds % 60);
    return `${minutes}:${String(rest).padStart(2, "0")}`;
};

const eventLabel = (event) => {
    if (event === "all" || event === "") return "Todos os tipos";
    return EVENT_TYPE_LABELS[event] || String(event || "").replace(/_/g, " ");
};

const sourceLabel = (event) => {
    return SOURCE_LABELS[event?.source_origin] || (event?.source_type === "screen" ? "Tela" : "Câmera normal");
};

const severityLabel = (severity) => {
    return SEVERITY_LABELS[severity] || severity || "-";
};

const formatDateTime = (value) => {
    return value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "-";
};

const jobStatusLabel = (status) => {
    if (status === "0") return "Pendente";
    if (status === "1") return "Executando";
    if (status === "2") return "Concluída";
    if (status === "3") return "Erro";
    return status || "Não gerada";
};

const normalizeFilter = (value) => (!value || value === "all" ? "" : value);

const VideoAIEvents = ({ processo_id, candidato_id = null, candidato_name = "", initial_source_origin = "all", initial_event_type = "all" }) => {
    const [loading, setLoading] = useState(true);
    const [loadingAction, setLoadingAction] = useState(false);
    const [search, setSearch] = useState("");
    const [searchOficial, setSearchOficial] = useState("");
    const [eventType, setEventType] = useState(initial_event_type || "all");
    const [sourceOrigin, setSourceOrigin] = useState(initial_source_origin || "all");
    const [sort, setSort] = useState("date_asc");
    const [groups, setGroups] = useState([]);
    const [videos, setVideos] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
    const [job, setJob] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const searchTimeout = useRef(null);
    const mode = candidato_id == null ? "processo" : "candidato";

    const loadEvents = async (page = 1, value = searchOficial, type = eventType, source = sourceOrigin, order = sort) => {
        setLoading(true);
        const query = `page=${page}&search=${encodeURIComponent(value)}&event_type=${encodeURIComponent(normalizeFilter(type))}&source_origin=${encodeURIComponent(normalizeFilter(source))}&sort=${encodeURIComponent(order)}`;
        const endpoint = mode === "processo"
            ? `faculdade/get_video_ai_events/${processo_id}?${query}`
            : `faculdade/get_video_ai_events_candidato/${processo_id}/${candidato_id}?${query}`;
        const response = await Request.Get(endpoint);

        if (response) {
            if (mode === "processo") {
                setGroups(response.candidatos || []);
            } else {
                setVideos(response.videos || []);
            }
            setPagination(response.pagination || { current_page: 1, last_page: 1 });
        }
        setLoading(false);
    };

    const loadJob = async () => {
        const response = await Request.Get(`faculdade/get_video_ai_events_job/${processo_id}`);
        if (response) setJob(response);
    };

    const generateEvents = async () => {
        setLoadingAction(true);
        const response = await Request.Post(`faculdade/generate_video_ai_events/${processo_id}`);
        if (response) setJob(response);
        setLoadingAction(false);
    };

    const downloadExcel = async () => {
        setLoadingAction(true);
        const query = `search=${encodeURIComponent(searchOficial)}&event_type=${encodeURIComponent(normalizeFilter(eventType))}&source_origin=${encodeURIComponent(normalizeFilter(sourceOrigin))}${candidato_id ? `&user_id=${candidato_id}` : ""}`;
        const response = await Request.GetFile(`faculdade/download_video_ai_events/${processo_id}?${query}`);
        if (response) {
            const filename = candidato_id
                ? `eventos_ia_videos_candidato_${candidato_id}_processo_${processo_id}.xlsx`
                : `eventos_ia_videos_processo_${processo_id}.xlsx`;
            saveAs(response, filename);
        }
        setLoadingAction(false);
    };

    useEffect(() => {
        loadEvents(1, "");
        loadJob();
    }, [processo_id, candidato_id]);

    const renderEventSummary = (items = []) => (
        <div className="video-ai-event-summary-list">
            {items.length === 0 ? (
                <span className="text-muted">-</span>
            ) : items.map((item) => (
                <span className="video-ai-badge event" key={item.event_type}>
                    {item.label || eventLabel(item.event_type)}: {item.count}
                </span>
            ))}
        </div>
    );

    const candidateDetailsLink = (candidateId) => {
        const params = new URLSearchParams();
        params.set("tab", "8");
        if (sourceOrigin && sourceOrigin !== "all") params.set("source_origin", sourceOrigin);
        if (eventType && eventType !== "all") params.set("event_type", eventType);
        return `/candidatos_vinculados/show/${candidateId}/${processo_id}?${params.toString()}`;
    };

    const showDetectedSiteColumn = mode === "candidato" && sourceOrigin === "screen";

    const renderGeneralRows = () => (
        <div className="table-modern-card video-ai-events-table">
            <div className="table-modern-scroll">
                <ResponsiveTable className="table table-striped">
                    <thead>
                        <tr>
                            <th>Imagem</th>
                            <th>Candidato</th>
                            <th>E-mail</th>
                            <th>CPF</th>
                            <th>Inscrição</th>
                            <th>Eventos</th>
                            <th>Pontuação</th>
                            <th className="hidethis">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((group) => (
                            <tr key={group.id}>
                                <td data-label="Imagem">
                                    {group.main_snapshot?.snapshot_url ? (
                                        <img className="video-ai-snapshot-thumb" src={group.main_snapshot.snapshot_url} alt={group.main_snapshot.label || "Imagem do evento"} />
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </td>
                                <td data-label="Candidato"><b>{group.name || "-"}</b></td>
                                <td data-label="E-mail">{group.email || "-"}</td>
                                <td data-label="CPF">{group.cpf || "-"}</td>
                                <td data-label="Inscrição">{group.numero_inscricao || "-"}</td>
                                <td data-label="Eventos">{renderEventSummary(group.event_summaries || [])}</td>
                                <td data-label="Pontuação"><span className="video-ai-badge points">{group.events_points || 0}</span></td>
                                <td className="hidethis" data-label="Ações">
                                    <Link className="btn btn-primary btn-sm video-ai-eye-button" to={candidateDetailsLink(group.id)} target="_blank" rel="noopener noreferrer">
                                        <BsFillEyeFill size={16} />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </ResponsiveTable>
            </div>
        </div>
    );

    const renderVideoRows = () => (
        <div className="table-modern-card video-ai-events-table">
            <div className="table-modern-scroll">
                <ResponsiveTable className="table table-striped">
                    <thead>
                        <tr>
                            <th>Imagem</th>
                            <th>Eventos</th>
                            {showDetectedSiteColumn && <th>Site detectado</th>}
                            <th>Origem</th>
                            <th>Trecho</th>
                            <th>Data/hora</th>
                            <th>Pontos</th>
                            <th className="hidethis">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {videos.map((video) => {
                            const firstSnapshot = (video.events || []).find((event) => event.snapshot_url);
                            const domains = [...new Set((video.events || []).map((event) => event.detected_domain).filter(Boolean))];
                            return (
                                <tr key={video.id}>
                                    <td data-label="Imagem">
                                        {firstSnapshot?.snapshot_url ? (
                                            <img className="video-ai-snapshot-thumb" src={firstSnapshot.snapshot_url} alt="Imagem do evento" />
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td data-label="Eventos">{renderEventSummary(video.event_summaries || [])}</td>
                                    {showDetectedSiteColumn && (
                                        <td data-label="Site detectado">
                                            {domains.length ? domains.map((domain) => <span className="video-ai-badge neutral" key={domain}>{domain}</span>) : "-"}
                                        </td>
                                    )}
                                    <td data-label="Origem">{sourceLabel(video)}</td>
                                    <td data-label="Trecho">{formatSeconds(video.start_seconds)} - {formatSeconds(video.end_seconds)}</td>
                                    <td data-label="Data/hora">{formatDateTime(video.event_at)}</td>
                                    <td data-label="Pontos"><span className="video-ai-badge points">{video.events_points || 0}</span></td>
                                    <td className="hidethis" data-label="Ações">
                                        <Button className="video-ai-eye-button" size="sm" variant="primary" onClick={() => setSelectedVideo(video)}>
                                            <BsFillEyeFill size={16} />
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </ResponsiveTable>
            </div>
        </div>
    );

    const renderEventDetails = () => {
        if (!selectedVideo) return null;
        const selectedEvents = selectedVideo.events || [];

        return (
            <Modal show={selectedVideo != null} onHide={() => setSelectedVideo(null)} size="xl" centered dialogClassName="video-ai-detail-modal">
                <Modal.Header closeButton closeLabel="">
                <Modal.Title>Detalhes dos eventos de IA</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="video-ai-detail-summary">
                        {renderEventSummary(selectedVideo.event_summaries || [])}
                        <span className="video-ai-badge neutral">{sourceLabel(selectedVideo)}</span>
                        <span className="video-ai-badge points">{selectedVideo.events_points || 0} pontos</span>
                    </div>

                    <div className="video-ai-detail-layout">
                        <div className="video-ai-detail-media">
                            <strong>{selectedVideo.video_url ? "Vídeo" : "Imagem"}</strong>
                            {selectedVideo.video_url ? (
                                <video className="video-ai-detail-video" src={selectedVideo.video_url} controls preload="metadata" />
                            ) : selectedVideo.image_url ? (
                                <img className="video-ai-detail-video" src={selectedVideo.image_url} alt="Imagem analisada" />
                            ) : (
                                <div className="video-ai-detail-empty">Mídia não disponível</div>
                            )}
                        </div>

                        <div className="video-ai-detail-info">
                            <div><span>Data/hora do primeiro evento</span><b>{formatDateTime(selectedVideo.event_at)}</b></div>
                            <div><span>Trecho analisado</span><b>{formatSeconds(selectedVideo.start_seconds)} - {formatSeconds(selectedVideo.end_seconds)}</b></div>
                            <div><span>Origem da análise</span><b>{sourceLabel(selectedVideo)}</b></div>
                            <div><span>Vídeo disponível</span><b>{selectedVideo.video_url ? "Sim" : "Não"}</b></div>
                            {selectedVideo.image_id && <div><span>Imagem ID</span><b>{selectedVideo.image_id}</b></div>}
                            <div><span>Total de eventos</span><b>{selectedVideo.events_count || 0}</b></div>
                        </div>
                    </div>

                    <div className="video-ai-detail-events">
                        <strong>Eventos detectados</strong>
                        {selectedEvents.map((event) => (
                            <div className="video-ai-detail-event-card" key={event.id}>
                                <div className="video-ai-detail-event-image">
                                    {event.snapshot_url ? (
                                        <img src={event.snapshot_url} alt={eventLabel(event.event_type)} />
                                    ) : (
                                        <div className="video-ai-detail-empty">Imagem não disponível</div>
                                    )}
                                </div>
                                <div className="video-ai-detail-event-content">
                                    <div className="video-ai-detail-event-title">
                                        <span className="video-ai-badge event">{eventLabel(event.event_type)}</span>
                                        <span className={`video-ai-severity ${event.severity}`}>{severityLabel(event.severity)}</span>
                                        <span className="video-ai-badge points">{event.points || 0} pontos</span>
                                    </div>
                                    <div className="video-ai-detail-info compact">
                                        <div><span>Data/hora</span><b>{formatDateTime(event.event_at)}</b></div>
                                        <div><span>Segundo da imagem</span><b>{formatSeconds(event.snapshot_seconds)}</b></div>
                                        <div><span>Trecho</span><b>{formatSeconds(event.start_seconds)} - {formatSeconds(event.end_seconds)}</b></div>
                                        {event.image_id && <div><span>Imagem ID</span><b>{event.image_id}</b></div>}
                                        {event.detected_domain && <div><span>Site detectado</span><b>{event.detected_domain}</b></div>}
                                        <div><span>ID do evento</span><b>{event.id || "-"}</b></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>
        );
    };

    return (
        <div className="video-ai-events">
            <div className="video-ai-events-header">
                <div>
                    <h4 className="header-title">
                        <MdOutlineSmartToy /> Eventos de IA dos vídeos
                    </h4>
                    <p className="text-muted">
                        {mode === "processo"
                            ? "Candidatos com eventos detectados, ordenados por maior quantidade de eventos."
                            : `Eventos detectados nos vídeos do candidato ${candidato_name}.`}
                    </p>
                    {job?.status && (
                        <p className="video-ai-job-status">
                            Status da análise: <b>{jobStatusLabel(job.status)}</b>
                            {job.total_progresso ? ` (${job.progresso || 0}/${job.total_progresso})` : ""}
                        </p>
                    )}
                </div>

                <div className="video-ai-actions">
                    {mode === "processo" && (
                        <Button variant="success" onClick={generateEvents} disabled={loadingAction}>
                            <MdPlayArrow /> Gerar eventos
                        </Button>
                    )}
                    <Button variant={mode === "candidato" ? "primary" : "secondary"} onClick={downloadExcel} disabled={loadingAction}>
                        <MdFileDownload /> Baixar Excel
                    </Button>
                    <Button variant="secondary" onClick={() => { loadJob(); loadEvents(pagination.current_page || 1, searchOficial, eventType, sourceOrigin, sort); }} disabled={loadingAction}>
                        <MdRefresh /> Atualizar
                    </Button>
                </div>
            </div>

            <div className="video-ai-toolbar hidethis">
                <div className="video-ai-filter-field search-field">
                    <label>{mode === "processo" ? "Pesquisar candidato" : "Pesquisar evento"}</label>
                    <Search
                        hide_class
                        placeholder={mode === "processo" ? "Nome, CPF ou inscrição" : "Evento, origem, risco ou site"}
                        ValueChange={(value) => {
                            clearTimeout(searchTimeout.current);
                            setSearch(value);
                            searchTimeout.current = setTimeout(() => {
                                setSearchOficial(value);
                                loadEvents(1, value, eventType, sourceOrigin, sort);
                            }, 500);
                        }}
                    />
                </div>
                <div className="video-ai-filter-field">
                    <label>Origem</label>
                    <select
                        className="form-control video-ai-event-type-filter"
                        value={sourceOrigin}
                        onChange={(event) => {
                            const value = event.target.value;
                            setSourceOrigin(value);
                            loadEvents(1, searchOficial, eventType, value, sort);
                        }}
                    >
                        {SOURCE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <div className="video-ai-filter-field">
                    <label>Tipo de evento</label>
                    <select
                        className="form-control video-ai-event-type-filter"
                        value={eventType}
                        onChange={(event) => {
                            const value = event.target.value;
                            setEventType(value);
                            loadEvents(1, searchOficial, value, sourceOrigin, sort);
                        }}
                    >
                        {EVENT_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                {mode === "candidato" && (
                    <div className="video-ai-filter-field">
                        <label>Ordenação</label>
                        <select
                            className="form-control video-ai-event-type-filter"
                            value={sort}
                            onChange={(event) => {
                                const value = event.target.value;
                                setSort(value);
                                loadEvents(1, searchOficial, eventType, sourceOrigin, value);
                            }}
                        >
                            {SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {loading && <div className="table-state-spacing text-center"><CircularProgress /></div>}

            {!loading && mode === "processo" && groups.length > 0 && renderGeneralRows()}

            {!loading && mode === "candidato" && videos.length > 0 && renderVideoRows()}

            {!loading && ((mode === "processo" && groups.length === 0) || (mode === "candidato" && videos.length === 0)) && (
                <div className="empty-state table-state-spacing">
                    <h4>Nenhum evento encontrado</h4>
                    <p>{search ? "Tente ajustar a busca." : "Gere os eventos para visualizar as análises dos vídeos."}</p>
                </div>
            )}

            {pagination.last_page > 1 && (
                <Pagination className="hidethis"
                    style={{ display: "flex", justifyContent: "center", marginTop: 24 }}
                    count={pagination.last_page}
                    page={pagination.current_page}
                    onChange={(_, value) => loadEvents(value, searchOficial, eventType, sourceOrigin, sort)}
                />
            )}

            {renderEventDetails()}
        </div>
    );
};

export default VideoAIEvents;
